/**
 * Download materials page — client-side filtering (category + subcategory +
 * language + search)
 * Data comes from `download_material` metaobjects rendered by
 * sections/at-download-materials.liquid. All rows live in the DOM; this script
 * controls visibility, the dependent subcategory dropdown, and "load more".
 *
 * Category/subcategory use a custom dropdown (button + listbox) instead of a
 * native <select> so the expanded options can be fully styled.
 */

/**
 * Ignore special polish letters so search is diacritic-insensitive
 *
 * @param {string} text text
 * @returns {string} formatted text
 */
function normalizeText(text) {
  const map = {
    ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
  };
  return (text || '')
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (match) => map[match])
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Debounce helper
 *
 * @param {Function} fn function to debounce
 * @param {number} wait delay in ms
 * @returns {Function} debounced function
 */
function debounce(fn, wait) {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/**
 * Enhance a custom dropdown (button + listbox) with open/close, selection,
 * outside-click and keyboard support.
 *
 * @param {HTMLElement} root dropdown root ([data-dl-dropdown])
 * @param {Function} onChange called with the selected value
 * @returns {Object|null} controller with getValue/setOptions/reset
 */
function createDropdown(root, onChange) {
  const toggle = root.querySelector('[data-dl-dropdown-toggle]');
  const valueEl = root.querySelector('[data-dl-dropdown-value]');
  const list = root.querySelector('[data-dl-dropdown-list]');
  if (!toggle || !valueEl || !list) return null;

  let value = '';

  /**
   * Open the option list.
   */
  function open() {
    list.hidden = false;
    root.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
  }

  /**
   * Close the option list.
   */
  function close() {
    list.hidden = true;
    root.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Select an option element and notify.
   *
   * @param {HTMLElement} option chosen [role=option]
   */
  function selectOption(option) {
    value = option.getAttribute('data-value') || '';
    // innerHTML so the language options keep their flag in the toggle; options
    // built by script are textContent-only, so nothing unescaped gets in.
    valueEl.innerHTML = option.innerHTML;
    list.querySelectorAll('[role="option"]').forEach((o) => {
      const selected = o === option;
      o.classList.toggle('is-selected', selected);
      o.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
    close();
    toggle.focus();
    if (onChange) onChange(value);
  }

  toggle.addEventListener('click', () => {
    if (list.hidden) open();
    else close();
  });

  list.addEventListener('click', (e) => {
    const option = e.target.closest('[role="option"]');
    if (option) selectOption(option);
  });

  root.addEventListener('keydown', (e) => {
    const options = Array.from(list.querySelectorAll('[role="option"]:not([hidden])'));
    if (e.key === 'Escape') {
      close();
      toggle.focus();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (list.hidden) open();
      const current = options.findIndex((o) => o.classList.contains('is-active'));
      let next = e.key === 'ArrowDown' ? current + 1 : current - 1;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      options.forEach((o, i) => o.classList.toggle('is-active', i === next));
      if (options[next]) options[next].scrollIntoView({ block: 'nearest' });
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && !list.hidden) {
      const active = list.querySelector('[role="option"].is-active');
      if (active) {
        e.preventDefault();
        selectOption(active);
      }
    }
  });

  document.addEventListener('click', (e) => {
    if (!root.contains(e.target)) close();
  });

  return {
    getValue: () => value,
    /**
     * Replace the option list (used for the dependent subcategory dropdown).
     *
     * @param {Array<{value: string, label: string}>} options options
     */
    setOptions(options) {
      list.innerHTML = '';
      options.forEach((opt, i) => {
        const li = document.createElement('li');
        li.className = `at-dl__select-option${i === 0 ? ' is-selected' : ''}`;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        li.setAttribute('data-value', opt.value);
        li.textContent = opt.label;
        list.appendChild(li);
      });
      value = options.length ? options[0].value : '';
      valueEl.textContent = options.length ? options[0].label : '';
      close();
    },
  };
}

/**
 * Rebuild the primary dropdown options from every loaded item (skipped when a
 * manual category order is configured). The axis is category or brand depending
 * on `primaryAttr`. Preserves the first "all" option and hides the whole field
 * when a single (or no) value makes the dropdown pointless (e.g. a subpage of
 * one type with a single brand).
 *
 * @param {HTMLElement} root section root
 * @param {string} primaryAttr item attribute to group by (data-category|data-brand)
 */
function rebuildPrimaryOptions(root, primaryAttr) {
  if (root.getAttribute('data-dl-category-manual') === 'true') return;
  const primaryRoot = root.querySelector('[data-dl-category]');
  if (!primaryRoot) return;
  const list = primaryRoot.querySelector('[data-dl-dropdown-list]');
  const allOption = list ? list.querySelector('[role="option"]') : null;
  if (!list || !allOption) return;

  const values = [];
  root.querySelectorAll('[data-dl-item]').forEach((item) => {
    const v = (item.getAttribute(primaryAttr) || '').trim();
    if (v && values.indexOf(v) === -1) values.push(v);
  });
  values.sort((a, b) => a.localeCompare(b, 'pl'));

  list.innerHTML = '';
  list.appendChild(allOption);
  values.forEach((v) => {
    const li = document.createElement('li');
    li.className = 'at-dl__select-option';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.setAttribute('data-value', v);
    li.textContent = v;
    list.appendChild(li);
  });

  primaryRoot.hidden = values.length <= 1;
}

/**
 * Language codes used by the rendered rows, in first-seen order.
 *
 * @param {HTMLElement} root section root
 * @returns {string[]} normalised codes (pl, en, ...)
 */
function collectLanguageCodes(root) {
  const codes = [];
  root.querySelectorAll('[data-dl-item]').forEach((item) => {
    (item.getAttribute('data-lang-codes') || '').split('|').forEach((code) => {
      if (code && codes.indexOf(code) === -1) codes.push(code);
    });
  });
  return codes;
}

/**
 * Prepare the language dropdown: hide the server-rendered options no row uses,
 * append codes the theme has no flag for, and swap the code fallback for the
 * language name in the visitor's locale (Intl, so no theme translations are
 * needed). Hides the whole field when a single (or no) language is present.
 *
 * @param {HTMLElement} root section root
 */
function prepareLanguageOptions(root) {
  const languageRoot = root.querySelector('[data-dl-language]');
  if (!languageRoot) return;
  const list = languageRoot.querySelector('[data-dl-dropdown-list]');
  if (!list) return;

  const codes = collectLanguageCodes(root);
  const rendered = [];
  list.querySelectorAll('[role="option"]').forEach((option) => {
    const code = option.getAttribute('data-value');
    if (!code) return;
    rendered.push(code);
    option.hidden = codes.indexOf(code) === -1;
  });
  codes.forEach((code) => {
    if (rendered.indexOf(code) !== -1) return;
    const li = document.createElement('li');
    li.className = 'at-dl__select-option';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.setAttribute('data-value', code);
    li.textContent = code.toUpperCase();
    list.appendChild(li);
  });

  let names = null;
  try {
    const locale = languageRoot.getAttribute('data-dl-locale') || document.documentElement.lang || 'pl';
    names = new Intl.DisplayNames([locale], { type: 'language' });
  } catch (e) {
    names = null;
  }
  if (names) {
    list.querySelectorAll('[role="option"]').forEach((option) => {
      const code = option.getAttribute('data-value');
      if (!code) return;
      const nameEl = option.querySelector('[data-dl-lang-name]') || option;
      let name = '';
      try {
        name = names.of(code) || '';
      } catch (e) {
        name = '';
      }
      // Intl echoes an unknown code back unchanged — keep the uppercase code then.
      if (name && name.toLowerCase() !== code) {
        nameEl.textContent = name.charAt(0).toLocaleUpperCase() + name.slice(1);
      }
    });
  }

  languageRoot.hidden = codes.length <= 1;
}

/**
 * Initialise a single download-materials section
 *
 * @param {HTMLElement} root section root element
 */
function initSection(root) {
  const categoryRoot = root.querySelector('[data-dl-category]');
  const languageRoot = root.querySelector('[data-dl-language]');
  const subcategoryField = root.querySelector('[data-dl-subcategory-field]');
  const subcategoryLabel = root.querySelector('[data-dl-subcategory-label]');
  const searchInput = root.querySelector('[data-dl-search]');
  const countEl = root.querySelector('[data-dl-count]');
  const emptyEl = root.querySelector('[data-dl-empty]');
  const loadMoreBtn = root.querySelector('[data-dl-loadmore]');

  if (!root.querySelector('[data-dl-item]')) return;

  const perPage = parseInt(root.getAttribute('data-per-page'), 10) || 10;
  const allSubLabel = subcategoryField ? (subcategoryField.getAttribute('data-dl-all-label') || '') : '';
  // Primary filter axis: 'category' (all-materials page) or 'brand' (type subpages).
  const primaryAttr = `data-${root.getAttribute('data-dl-primary') || 'category'}`;

  /**
   * Live list of all rows currently in the DOM (grows as pages load).
   *
   * @returns {HTMLElement[]} row elements
   */
  const getItems = () => Array.from(root.querySelectorAll('[data-dl-item]'));

  const state = {
    category: '',
    subcategory: '',
    language: '',
    search: '',
    visibleCount: perPage,
  };

  /**
   * Whether an item passes the current filters (ignoring the visible-count cap).
   *
   * @param {HTMLElement} item row element
   * @returns {boolean} matches
   */
  function matches(item) {
    if (state.category && item.getAttribute(primaryAttr) !== state.category) return false;
    if (state.subcategory && (item.getAttribute('data-subcategory') || '').trim() !== state.subcategory) return false;
    if (state.language && (item.getAttribute('data-lang-codes') || '').indexOf(`|${state.language}|`) === -1) return false;
    if (state.search) {
      const haystack = normalizeText(`${item.getAttribute('data-title') || ''} ${item.getAttribute('data-keywords') || ''}`);
      if (!haystack.includes(state.search)) return false;
    }
    return true;
  }

  /**
   * Apply filters + visible-count cap and update UI.
   */
  function applyFilters() {
    let matched = 0;
    let shown = 0;

    getItems().forEach((item) => {
      if (matches(item)) {
        matched += 1;
        if (shown < state.visibleCount) {
          item.classList.remove('at-dl__item--hidden');
          shown += 1;
        } else {
          item.classList.add('at-dl__item--hidden');
        }
      } else {
        item.classList.add('at-dl__item--hidden');
      }
    });

    if (countEl) {
      const tpl = countEl.getAttribute('data-template') || '%COUNT%';
      countEl.textContent = tpl.replace('%COUNT%', matched);
    }
    if (emptyEl) emptyEl.hidden = matched !== 0;
    if (loadMoreBtn) loadMoreBtn.hidden = matched <= state.visibleCount;
  }

  const subcategoryDropdown = subcategoryField
    ? createDropdown(subcategoryField, (val) => {
      state.subcategory = val;
      state.visibleCount = perPage;
      applyFilters();
    })
    : null;

  /**
   * Rebuild the subcategory dropdown from the items of the selected category.
   */
  function buildSubcategoryOptions() {
    if (!subcategoryDropdown || !subcategoryField) return;

    if (!state.category) {
      subcategoryField.hidden = true;
      subcategoryDropdown.setOptions([{ value: '', label: allSubLabel }]);
      state.subcategory = '';
      return;
    }

    const subs = [];
    getItems().forEach((item) => {
      if (item.getAttribute(primaryAttr) !== state.category) return;
      const sub = (item.getAttribute('data-subcategory') || '').trim();
      if (sub && subs.indexOf(sub) === -1) subs.push(sub);
    });
    subs.sort((a, b) => a.localeCompare(b, 'pl'));

    const options = [{ value: '', label: allSubLabel }].concat(
      subs.map((sub) => ({ value: sub, label: sub })),
    );
    subcategoryDropdown.setOptions(options);

    state.subcategory = '';
    subcategoryField.hidden = subs.length === 0;
    if (subcategoryLabel) subcategoryLabel.textContent = state.category;
  }

  if (categoryRoot) {
    createDropdown(categoryRoot, (val) => {
      state.category = val;
      state.visibleCount = perPage;
      buildSubcategoryOptions();
      applyFilters();
    });
    rebuildPrimaryOptions(root, primaryAttr);
  }

  if (languageRoot) {
    prepareLanguageOptions(root);
    createDropdown(languageRoot, (val) => {
      state.language = val;
      state.visibleCount = perPage;
      applyFilters();
    });
  }

  if (searchInput) {
    searchInput.addEventListener('input', debounce((e) => {
      state.search = normalizeText(e.target.value);
      state.visibleCount = perPage;
      applyFilters();
    }, 150));
  }

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', () => {
      state.visibleCount += perPage;
      applyFilters();
    });
  }

  applyFilters();
}

/**
 * Init all sections on the page
 */
function init() {
  document.querySelectorAll('.at-dl').forEach((root) => initSection(root));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
